/**
 * Marketplace Offer — canonical type + lifecycle (P1).
 *
 * Additive to the legacy `ContractorOpportunity` (bid-style record), which is
 * left untouched. Offers represent a one-shot, time-boxed assignment proposal
 * sent to a SINGLE eligible contractor for a single job.
 *
 * Status is event-driven — resolveOfferStatus() is the only path that may
 * transition `pending → expired`, and it emits a canonical `offer_expired`
 * SystemEvent + appends to the immutable audit trail.
 *
 * Pricing/financials are intentionally absent. Offers carry `payoutAmount`
 * (contractor pay) only when the job already has one — never `contractValue`.
 *
 * See mem://finance/canonical-pricing — write-path policy forbids any sync
 * between contractValue and payoutAmount; this module never touches either.
 */

export type MarketplaceOfferStatus =
  | "pending"
  | "accepted"
  | "declined"
  | "expired"
  | "withdrawn"
  | "superseded";

export type OfferAuditEvent =
  | "offer_created"
  | "offer_accepted"
  | "offer_declined"
  | "offer_expired"
  | "offer_withdrawn"
  | "offer_superseded"
  | "offer_extended"
  | "offer_window_changed";

export interface OfferAuditEntry {
  /** Append-only. */
  event: OfferAuditEvent;
  at: string;
  /** Who initiated — system / dispatcher userId / contractorId. */
  actor: string;
  reason?: string;
  /** Snapshot of status AFTER this entry was applied. */
  statusAfter: MarketplaceOfferStatus;
}

/**
 * Typed, enumerated decline reasons. Free-text notes are captured separately
 * via `responseNote`. Decline UIs MUST pick from this list — open strings
 * fragment analytics and break Smart Dispatch's "why this contractor keeps
 * declining" intelligence.
 */
export type OfferDeclineReason =
  | "schedule_conflict"
  | "out_of_area"
  | "scope_mismatch"
  | "capacity"
  | "payout_too_low"
  | "materials_concern"
  | "customer_risk"
  | "other";

export const OFFER_DECLINE_REASONS: ReadonlyArray<{
  value: OfferDeclineReason;
  label: string;
}> = [
  { value: "schedule_conflict", label: "Schedule conflict" },
  { value: "out_of_area", label: "Out of service area" },
  { value: "scope_mismatch", label: "Scope mismatch" },
  { value: "capacity", label: "No crew capacity" },
  { value: "payout_too_low", label: "Payout too low" },
  { value: "materials_concern", label: "Materials concern" },
  { value: "customer_risk", label: "Customer risk flag" },
  { value: "other", label: "Other" },
];

export interface MarketplaceOffer {
  id: string;
  jobId: string;
  organizationId?: string;
  contractorId: string;
  status: MarketplaceOfferStatus;
  sentAt: string;
  sentBy: string;
  expiresAt: string;
  /** Contractor payout snapshot at offer time (display only). */
  payoutAmountSnapshot?: number;
  respondedAt?: string;
  /** Typed decline reason (declined offers only). */
  responseReason?: OfferDeclineReason;
  /** Optional free-text note appended to a typed decline reason. */
  responseNote?: string;
  /** Append-only history. resolveOfferStatus + engine writers append; consumers must never edit. */
  audit: OfferAuditEntry[];
}

export const DEFAULT_OFFER_TTL_MINUTES = 120;

function fallbackUuidLike(): string {
  const hex = () => Math.floor(Math.random() * 0xffffffff).toString(16).padStart(8, "0");
  const a = hex();
  const b = hex().slice(0, 4);
  const c = `4${hex().slice(1, 4)}`;
  const d = ((8 + Math.floor(Math.random() * 4)).toString(16) + hex().slice(1, 4));
  const e = `${hex()}${hex().slice(0, 4)}`;
  return `${a}-${b}-${c}-${d}-${e}`;
}

export function generateOfferId(): string {
  const cryptoObj = globalThis.crypto as Crypto | undefined;
  if (cryptoObj?.randomUUID) return cryptoObj.randomUUID();
  return fallbackUuidLike();
}
